import { useCallback, useEffect, useRef, useState } from "react";
import { getApiBaseUrl } from "../../../shared/lib/api-client.ts";
import { getRankClass, getScoreClass } from "../../../shared/lib/scoring-utils.ts";
import { useLeaderboardFilters } from "../hooks/use-leaderboard-filters.ts";
import {
  PAGE_SIZE,
  useCategoriesQuery,
  useProjectsQuery,
} from "../hooks/use-leaderboard-queries.ts";
import { filtersToQueryParams } from "../types/filters.ts";
import type { Project } from "../types/index.ts";
import { LeaderboardActiveFilters } from "./LeaderboardActiveFilters.tsx";
import { LeaderboardFilterPanel } from "./LeaderboardFilters.tsx";
import { LeaderboardQuickFilters } from "./LeaderboardQuickFilters.tsx";

const toNum = (v: number | string | null) => (v != null ? Number(v) : 0);

interface LeaderboardViewProps {
  onOpenProjectDetail: (project: Project) => void;
}

interface CategoryDelta {
  label: string;
  prev: number;
  now: number;
  delta: number;
}

function getCategoryDeltas(project: Project): {
  deltas: CategoryDelta[];
  hasPrevData: boolean;
} {
  const categories: {
    label: string;
    now: number | string | null;
    prev: number | string | null | undefined;
  }[] = [
    { label: "Social", now: project.social_points, prev: project.prev_social_points },
    { label: "Backers", now: project.backers_points, prev: project.prev_backers_points },
    { label: "Pedigree", now: project.pedigree_points, prev: project.prev_pedigree_points },
    { label: "Momentum", now: project.momentum_points, prev: project.prev_momentum_points },
    { label: "Dev", now: project.dev_points, prev: project.prev_dev_points },
    { label: "Revenue", now: project.revenue_points, prev: project.prev_revenue_points },
  ];
  const deltas: CategoryDelta[] = [];
  let hasPrevData = false;
  for (const cat of categories) {
    if (cat.prev == null) continue;
    hasPrevData = true;
    const now = toNum(cat.now);
    const prev = toNum(cat.prev);
    const delta = +(now - prev).toFixed(1);
    if (delta !== 0) deltas.push({ label: cat.label, prev, now, delta });
  }
  return { deltas, hasPrevData };
}

function RankChangePopover({ project }: { project: Project }) {
  const { deltas, hasPrevData } = getCategoryDeltas(project);
  const hasPrevAlpha = project.prev_alpha_score != null;
  if (!hasPrevData && !hasPrevAlpha) return null;

  const prevTotal = toNum(project.prev_alpha_score ?? null);
  const nowTotal = toNum(project.current_alpha_score);
  const totalDelta = +(nowTotal - prevTotal).toFixed(1);

  if (deltas.length === 0 && totalDelta === 0) {
    return (
      <div className="rank-popover">
        <div className="rank-popover-hint">Scores unchanged — rank shifted by other projects</div>
      </div>
    );
  }
  return (
    <div className="rank-popover">
      <div className="rank-popover-header">
        <span>Category</span>
        <span>7d ago</span>
        <span>Now</span>
        <span>Delta</span>
      </div>
      <div className="rank-popover-row rank-popover-total">
        <span className="rank-popover-label">Total</span>
        <span className="rank-popover-val">{prevTotal.toFixed(1)}</span>
        <span className="rank-popover-val">{nowTotal.toFixed(1)}</span>
        <span
          className={`rank-popover-delta ${totalDelta > 0 ? "rank-popover-up" : totalDelta < 0 ? "rank-popover-down" : ""}`}
        >
          {totalDelta > 0 ? `+${totalDelta.toFixed(1)}` : totalDelta.toFixed(1)}
        </span>
      </div>
      {deltas.map((d) => (
        <div key={d.label} className="rank-popover-row">
          <span className="rank-popover-label">{d.label}</span>
          <span className="rank-popover-val">{d.prev.toFixed(1)}</span>
          <span className="rank-popover-val">{d.now.toFixed(1)}</span>
          <span
            className={`rank-popover-delta ${d.delta > 0 ? "rank-popover-up" : "rank-popover-down"}`}
          >
            {d.delta > 0 ? `+${d.delta.toFixed(1)}` : d.delta.toFixed(1)}
          </span>
        </div>
      ))}
    </div>
  );
}

function RankBadge({ project }: { project: Project }) {
  const [hovered, setHovered] = useState(false);
  const rankChange = project.rank_change;

  if (rankChange === null || rankChange === undefined) {
    return <span className="rank-badge rank-new">NEW</span>;
  }
  const num = Number(rankChange);
  if (num === 0) return <span className="rank-no-change">&mdash;</span>;

  const badge =
    num > 0 ? (
      <span className="rank-badge rank-up">
        <span className="rank-arrow">&#x25B2;</span>
        {num}
      </span>
    ) : (
      <span className="rank-badge rank-down">
        <span className="rank-arrow">&#x25BC;</span>
        {Math.abs(num)}
      </span>
    );

  return (
    // biome-ignore lint/a11y/noStaticElementInteractions: hover-only popover, no keyboard interaction needed
    // biome-ignore lint/a11y/useKeyWithClickEvents: stopPropagation only, not a real click handler
    <span
      className="rank-badge-wrapper"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onClick={(e) => e.stopPropagation()}
    >
      {badge}
      {hovered && <RankChangePopover project={project} />}
    </span>
  );
}

function useDebounce(value: string, delay: number) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);
  return debounced;
}

export function LeaderboardView({ onOpenProjectDetail }: LeaderboardViewProps) {
  const { filters, setFilters, patchFilters, applyPreset, clearPreset, resetAll } =
    useLeaderboardFilters();
  const [currentPage, setCurrentPage] = useState(1);
  const [searchInput, setSearchInput] = useState<string>(() => filters.search ?? "");
  const debouncedSearch = useDebounce(searchInput, 300);
  const searchRef = useRef<HTMLInputElement>(null);

  const [sortColumn, setSortColumn] = useState<string>(() => {
    try {
      return localStorage.getItem("lb-sort-col") ?? "total";
    } catch {
      return "total";
    }
  });
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">(() => {
    try {
      return (localStorage.getItem("lb-sort-dir") as "asc" | "desc") ?? "desc";
    } catch {
      return "desc";
    }
  });

  // Sync the debounced search input into the persisted filter object.
  // biome-ignore lint/correctness/useExhaustiveDependencies: debouncedSearch is the intentional trigger
  useEffect(() => {
    const trimmed = debouncedSearch.trim();
    setFilters((prev) => (trimmed ? { ...prev, search: trimmed } : { ...prev, search: undefined }));
    setCurrentPage(1);
  }, [debouncedSearch]);

  // Page resets are triggered explicitly by each filter mutation
  // (presets / patches / reset). We don't need an effect for that —
  // having one with `activeCount` as a dep double-fires.

  // Keep the search input in sync when filters change via preset/reset.
  // biome-ignore lint/correctness/useExhaustiveDependencies: only react to external filter mutations on filters.search; searchInput is read but the effect must not re-fire when the user types
  useEffect(() => {
    if ((filters.search ?? "") !== searchInput) {
      setSearchInput(filters.search ?? "");
    }
  }, [filters.search]);

  const handleCategoryChange = useCallback(
    (value: string) => {
      patchFilters({ category: value === "all" ? undefined : value });
      setCurrentPage(1);
    },
    [patchFilters]
  );

  const categoriesQuery = useCategoriesQuery();
  const projectsQuery = useProjectsQuery({
    page: currentPage,
    sort: sortColumn,
    direction: sortDirection,
    filters,
  });
  const categories = categoriesQuery.data ?? [];
  const projects = projectsQuery.data?.data ?? [];
  const pagination = projectsQuery.data?.pagination;
  const total = pagination?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const loading = projectsQuery.isLoading;
  const offset = pagination?.offset ?? 0;

  function handleSort(column: string) {
    let newCol: string;
    let newDir: "asc" | "desc";
    if (sortColumn === column) {
      newCol = column;
      newDir = sortDirection === "desc" ? "asc" : "desc";
    } else {
      newCol = column;
      newDir = "desc";
    }
    setSortColumn(newCol);
    setSortDirection(newDir);
    setCurrentPage(1);
    try {
      localStorage.setItem("lb-sort-col", newCol);
      localStorage.setItem("lb-sort-dir", newDir);
    } catch {
      /* ignore */
    }
  }

  function SortHeader({ column, label }: { column: string; label: string }) {
    const isActive = sortColumn === column;
    return (
      <button
        type="button"
        className={`sort-header ${isActive ? "active" : ""}`}
        onClick={() => handleSort(column)}
      >
        {label}
        {isActive ? (
          <span className="sort-arrow">{sortDirection === "desc" ? "↓" : "↑"}</span>
        ) : (
          <span className="sort-arrow-inactive">↕</span>
        )}
      </button>
    );
  }

  const rangeStart = total === 0 ? 0 : offset + 1;
  const rangeEnd = Math.min(offset + projects.length, total);

  const exportUrl = () => {
    const url = new URL(`${getApiBaseUrl()}/v1/projects/export`);
    url.searchParams.set("format", "csv");
    for (const [k, v] of Object.entries(filtersToQueryParams(filters))) {
      url.searchParams.set(k, v);
    }
    return url.toString();
  };

  return (
    <div className="leaderboard-section">
      <div className="section-header">
        <span className="section-title">Rankings</span>
        <div className="section-controls">
          <input
            ref={searchRef}
            type="text"
            className="leaderboard-search"
            placeholder="Search projects..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
          />
          <select
            className="category-filter"
            value={filters.category ?? "all"}
            onChange={(e) => handleCategoryChange(e.target.value)}
          >
            <option value="all">All Categories</option>
            {categories.map((cat) => (
              <option key={cat.slug} value={cat.slug}>
                {cat.name} ({cat.project_count})
              </option>
            ))}
          </select>
          <button
            type="button"
            className="export-btn"
            onClick={() =>
              (globalThis as { open?: (url?: string, target?: string) => void }).open?.(
                exportUrl(),
                "_blank"
              )
            }
          >
            Export CSV
          </button>
          <span className="section-count">
            {total > 0 ? `${rangeStart}-${rangeEnd} of ${total}` : "0 projects"}
          </span>
        </div>
      </div>

      <LeaderboardQuickFilters
        filters={filters}
        onApplyPreset={(id) => {
          applyPreset(id);
          setCurrentPage(1);
        }}
        onClearPreset={(id) => {
          clearPreset(id);
          setCurrentPage(1);
        }}
      />
      <LeaderboardFilterPanel
        filters={filters}
        onChange={(patch) => {
          patchFilters(patch);
          setCurrentPage(1);
        }}
      />
      <LeaderboardActiveFilters
        filters={filters}
        onPatch={(patch) => {
          patchFilters(patch);
          setCurrentPage(1);
        }}
        onResetAll={() => {
          resetAll();
          setCurrentPage(1);
        }}
      />

      <div className="leaderboard">
        <div className="leaderboard-header">
          <div>#</div>
          <div>Project</div>
          <div>Category</div>
          <SortHeader column="total" label="Total" />
          <SortHeader column="social" label="Social" />
          <SortHeader column="backers" label="Backers" />
          <SortHeader column="pedigree" label="Pedigree" />
          <SortHeader column="momentum" label="Momentum" />
          <SortHeader column="dev" label="Dev" />
          <SortHeader column="revenue" label="Revenue" />
          <SortHeader column="trend" label="Move" />
        </div>

        {loading ? (
          ["a", "b", "c", "d", "e", "f", "g", "h"].map((id) => (
            <div key={`skel-${id}`} className="skeleton-row">
              <div className="skeleton-line skeleton-rank" />
              <div className="skeleton-line skeleton-name" />
              <div className="skeleton-line skeleton-category" />
              <div className="skeleton-line skeleton-score" />
              <div className="skeleton-line skeleton-score" />
              <div className="skeleton-line skeleton-score" />
              <div className="skeleton-line skeleton-score" />
              <div className="skeleton-line skeleton-score" />
              <div className="skeleton-line skeleton-score" />
              <div className="skeleton-line skeleton-score" />
              <div className="skeleton-line skeleton-score" />
            </div>
          ))
        ) : projects.length === 0 ? (
          <div className="empty-state">
            {debouncedSearch ? (
              <>
                <h3>No results</h3>
                <p>No projects match "{debouncedSearch}"</p>
              </>
            ) : (
              <>
                <h3>No projects yet</h3>
                <p>Enter a Twitter handle above to score your first project</p>
              </>
            )}
          </div>
        ) : (
          projects.map((project, index) => {
            return (
              // biome-ignore lint/a11y/useSemanticElements: row keeps composite click/keyboard behavior while containing nested links
              <div
                key={project.id}
                className="leaderboard-row"
                role="button"
                tabIndex={0}
                onClick={() => onOpenProjectDetail(project)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") onOpenProjectDetail(project);
                }}
              >
                <div className={`rank ${getRankClass(offset + index)}`}>
                  {String(offset + index + 1).padStart(2, "0")}
                </div>
                <div className="project-info">
                  <span className="project-name">{project.name ?? project.twitter_handle}</span>
                  <a
                    href={`https://twitter.com/${project.twitter_handle}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="handle"
                    onClick={(e) => e.stopPropagation()}
                  >
                    @{project.twitter_handle}
                  </a>
                </div>
                <div className="category-cell">
                  {project.category_name ? (
                    <span
                      className="category-badge"
                      style={{ backgroundColor: project.category_color ?? "#6b7280" }}
                    >
                      {project.category_name}
                    </span>
                  ) : (
                    <span className="category-badge uncategorized">—</span>
                  )}
                </div>
                <div className={`alpha-score ${getScoreClass(project.current_alpha_score)}`}>
                  {project.current_alpha_score != null
                    ? Number(project.current_alpha_score).toFixed(1)
                    : "—"}
                </div>
                <div className="score-cell social">
                  <span className="score-value">{toNum(project.social_points).toFixed(1)}</span>
                </div>
                <div className="score-cell backers">
                  <span className="score-value">{toNum(project.backers_points).toFixed(1)}</span>
                </div>
                <div className="score-cell pedigree">
                  <span className="score-value">{toNum(project.pedigree_points).toFixed(1)}</span>
                </div>
                <div className="score-cell momentum">
                  <span className="score-value">{toNum(project.momentum_points).toFixed(1)}</span>
                </div>
                <div className="score-cell dev">
                  <span className="score-value">{toNum(project.dev_points).toFixed(1)}</span>
                </div>
                <div className="score-cell revenue">
                  <span className="score-value">{toNum(project.revenue_points).toFixed(1)}</span>
                </div>
                <div className="trend-cell">
                  <RankBadge project={project} />
                </div>
              </div>
            );
          })
        )}
      </div>

      {totalPages > 1 && (
        <div className="pagination-bar">
          <button
            type="button"
            className="pagination-btn"
            disabled={currentPage <= 1}
            onClick={() => setCurrentPage(1)}
          >
            First
          </button>
          <button
            type="button"
            className="pagination-btn"
            disabled={currentPage <= 1}
            onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
          >
            Prev
          </button>
          <span className="pagination-info">
            Page {currentPage} of {totalPages}
          </span>
          <button
            type="button"
            className="pagination-btn"
            disabled={currentPage >= totalPages}
            onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
          >
            Next
          </button>
          <button
            type="button"
            className="pagination-btn"
            disabled={currentPage >= totalPages}
            onClick={() => setCurrentPage(totalPages)}
          >
            Last
          </button>
        </div>
      )}
    </div>
  );
}
